/**
 * Utility functions for milestone styling
 */

/**
 * Generate a background color/tint from a milestone color
 * This creates a semi-transparent version of the color for row backgrounds
 * @param color - The hex color of the milestone (e.g., '#8d70d3')
 * @returns RGBA color string for background (e.g., 'rgba(141, 112, 211, 0.1)')
 */
export function getMilestoneBackgroundColor(color: string | undefined): string | undefined {
  if (!color) return undefined;
  
  // Convert hex to RGB
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Return semi-transparent background color
  // Using 0.08 opacity for subtle tinting
  return `rgba(${r}, ${g}, ${b}, 0.08)`;
}

/**
 * Generate a gradient background for milestones (for future use)
 * @param color - The hex color of the milestone
 * @returns CSS gradient string
 */
export function getMilestoneGradientBackground(color: string | undefined): string | undefined {
  if (!color) return undefined;
  
  const bgColor = getMilestoneBackgroundColor(color);
  if (!bgColor) return undefined;
  
  // Create a subtle gradient from transparent to the background color
  return `linear-gradient(90deg, transparent 0%, ${bgColor} 10%, ${bgColor} 90%, transparent 100%)`;
}

/**
 * Get milestone style object for consistent styling across components
 * Creates a gradient background that spans from the Project column to before Status column
 * @param color - The hex color of the milestone
 * @returns Style object for the milestone row with gradient and white text
 */
export function getMilestoneStyles(color: string | undefined): React.CSSProperties {
  if (!color) return {};
  
  // Convert hex to RGB for gradient
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Simple gradient with reduced intensity (30% less opacity)
  // Sharp edge at 8%, continuous fade to 45%
  const gradient = `linear-gradient(90deg, 
    transparent 0%, 
    transparent 8%, 
    rgba(${r}, ${g}, ${b}, 0.5) 8%, 
    rgba(${r}, ${g}, ${b}, 0.35) 15%, 
    rgba(${r}, ${g}, ${b}, 0.2) 25%, 
    rgba(${r}, ${g}, ${b}, 0.1) 35%,
    transparent 45%)`;
  
  return {
    background: gradient,
    color: 'white'
  };
}